// Authentication utility functions

/**
 * Decode JWT token without verification (for expiration check only)
 * Note: This does NOT verify the signature - backend will verify
 */
const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
};

/**
 * Get user information from JWT token
 * Returns user details that backend can also extract from the token
 */
export const getUserInfoFromToken = () => {
  const token = getAuthToken();
  if (!token) return null;

  try {
    const decoded = decodeJWT(token);
    if (!decoded) return null;

    return {
      userId: decoded.sub, // Cognito User ID (unique identifier)
      email: decoded.email,
      emailVerified: decoded.email_verified,
      username: decoded["cognito:username"] || decoded.username,
      groups: decoded["cognito:groups"] || [],
      authTime: decoded.auth_time ? new Date(decoded.auth_time * 1000) : null,
      tokenIssuedAt: decoded.iat ? new Date(decoded.iat * 1000) : null,
      tokenExpiresAt: decoded.exp ? new Date(decoded.exp * 1000) : null,
    };
  } catch (error) {
    console.error("Error extracting user info from token:", error);
    return null;
  }
};

/**
 * Get the friendly startup/brand name from localStorage (what the creator typed).
 * Use this for display purposes only.
 */
export const getStartupName = () => {
  return localStorage.getItem("STARTUP");
};

/**
 * Get the tenantId used for all DynamoDB/API operations.
 * Phase 2 migration: returns the Cognito sub UUID directly from JWT.
 * This is permanent, device-independent, and never requires user input.
 *
 * Falls back to legacy TENANT_ID in localStorage if JWT is unavailable
 * (e.g. token not yet loaded). Phase 3 data migration will rewrite all
 * DB records from {friendlyName}-{uid8} → full sub UUID.
 */
export const getTenantId = () => {
  // Prefer sub UUID from JWT — available on every authenticated session
  const userInfo = getUserInfoFromToken();
  if (userInfo?.userId) return userInfo.userId;
  // Fallback: legacy stored tenantId (before Phase 3 migration)
  return localStorage.getItem("TENANT_ID") || null;
};

/**
 * Check if JWT token is expired
 */
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return true;

    // exp is in seconds, Date.now() is in milliseconds
    const expirationTime = decoded.exp * 1000;
    const currentTime = Date.now();

    // Add 5 minute buffer to account for clock skew
    const buffer = 5 * 60 * 1000; // 5 minutes

    return currentTime >= expirationTime - buffer;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true; // If we can't decode, consider it expired
  }
};

/**
 * Get token expiration time in milliseconds
 */
export const getTokenExpirationTime = (token) => {
  if (!token) return null;

  try {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return null;

    return decoded.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error("Error getting token expiration:", error);
    return null;
  }
};

/**
 * Close all modals and UI overlays by dispatching a custom event
 * Components can listen to this event to close their modals
 */
const closeAllModals = () => {
  // Dispatch a custom event that components can listen to
  const event = new CustomEvent("forceCloseAllModals", {
    detail: { reason: "logout" }
  });
  window.dispatchEvent(event);
  
  // Also try to close common modal patterns via DOM
  // Close any elements with common modal classes
  const modals = document.querySelectorAll(
    '[class*="modal"], [class*="Modal"], [class*="dialog"], [class*="Dialog"], [role="dialog"]'
  );
  modals.forEach((modal) => {
    // Hide modal by setting display none or removing from DOM
    if (modal.style) {
      modal.style.display = "none";
    }
  });
};

export const clearAuthState = () => {
  // Close all modals and UI overlays first
  closeAllModals();

  // Clear all authentication-related localStorage items
  try {
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("cognitoUser");
    localStorage.removeItem("aws-amplify-cache");
    localStorage.removeItem("amplify-signin-with-hostedUI");
    localStorage.removeItem("amplify-signin-with-hostedUI-state");
    localStorage.removeItem("lastActivityTime");

    // Clear any other potential AWS Cognito cache items
    Object.keys(localStorage).forEach((key) => {
      if (
        key.includes("cognito") ||
        key.includes("amplify") ||
        key.includes("aws")
      ) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) { /* localStorage unavailable (sandboxed context) */ }

  console.log("Auth state cleared - all modals closed");
};

/**
 * Check if user is authenticated with a valid (non-expired) token
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem("jwtToken");
  if (!token) return false;

  // Check if token is expired
  if (isTokenExpired(token)) {
    console.warn("Token is expired, clearing auth state");
    clearAuthState();
    return false;
  }

  return true;
};

/**
 * Get auth token if it's valid (not expired)
 * Returns null if token is expired or missing
 */
export const getAuthToken = () => {
  const token = localStorage.getItem("jwtToken");
  if (!token) return null;

  // Check if token is expired
  if (isTokenExpired(token)) {
    console.warn("Token is expired, cannot use for authentication");
    clearAuthState();
    return null;
  }

  return token;
};

export const setAuthToken = (token) => {
  localStorage.setItem("jwtToken", token);
  // Update last activity time when token is set
  localStorage.setItem("lastActivityTime", Date.now().toString());
};
